import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Observable } from 'rxjs';

import { TurnstileService } from '../shared/services/turnstile.service';
import {
  Plan, SignupApiService, SignupCompany, SignupLead, SignupProgress, SignupResult, apiError
} from './signup-api.service';

type Stage = 'loading' | 'invalid' | 'account' | 'login' | 'company' | 'plan' | 'personal' | 'contract' | 'done';

export const WIZARD_STEPS: { stage: Stage; label: string }[] = [
  { stage: 'company', label: 'Empresa' },
  { stage: 'plan', label: 'Plano' },
  { stage: 'personal', label: 'Seus dados' },
  { stage: 'contract', label: 'Contrato' },
];

// ncommerce.app/cadastro/:token, the link sent to the lead: account (password
// or Google), then company and PC, plan, personal data and contract; the end
// shows the install code and the installer download.
@Component({
    selector: 'app-signup',
    templateUrl: './signup.component.html',
    styleUrls: ['./signup.scss'],
    standalone: false
})
export class SignupComponent implements OnInit {
  readonly steps = WIZARD_STEPS;
  readonly operatingSystems = [
    { key: 'ubuntu', label: 'Ubuntu', hint: 'Recomendado: versão 26 ou mais recente' },
    { key: 'windows11', label: 'Windows 11', hint: '' },
    { key: 'windows10', label: 'Windows 10', hint: 'Versão 22H2 (build 19045) ou mais recente' },
    { key: 'mac', label: 'Mac (Apple)', hint: 'Ainda não suportado' },
  ];
  readonly ubuntuCheck = '. /etc/os-release; echo "Sistema: $PRETTY_NAME"; echo "Processadores: $(nproc)"; ' +
    'awk \'/MemTotal/ {printf "Memória: %.1f GB\\n", $2/1048576}\' /proc/meminfo';

  readonly onPhone = /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
  readonly onMac = /Macintosh|Mac OS X/i.test(navigator.userAgent) && !/iPhone|iPad|iPod/i.test(navigator.userAgent);

  token = '';
  stage: Stage = 'loading';
  loading = false;
  error = '';

  lead: SignupLead | null = null;
  progress: SignupProgress | null = null;
  result: SignupResult | null = null;

  // account / login
  email = '';
  password = '';
  passwordConfirmation = '';

  company: SignupCompany = this.blankCompany();
  selectedPlan = '';
  personal = { name: '', cpf: '', whatsapp: '' };
  accepted = false;

  constructor(private route: ActivatedRoute, private api: SignupApiService, private turnstile: TurnstileService) { }

  ngOnInit(): void {
    this.token = this.route.snapshot.paramMap.get('token') || '';
    this.api.lead(this.token).subscribe({
      next: lead => {
        this.lead = lead;
        this.email = lead.email;
        if (this.api.hasAuth()) {
          this.loadProgress();
        } else {
          this.stage = lead.account ? 'login' : 'account';
        }
      },
      error: err => {
        this.error = apiError(err, 'Link inválido ou expirado. Peça um novo em ncommerce.app/cadastro.');
        this.stage = 'invalid';
      }
    });
  }

  // --- account -----------------------------------------------------------

  createAccount(): void {
    if (this.password.length < 6 || this.password !== this.passwordConfirmation) {
      this.error = 'A senha precisa ter pelo menos 6 caracteres e ser igual à confirmação.';
      return;
    }
    this.run(this.api.createAccount(this.token, {
      email: this.email, password: this.password, password_confirmation: this.passwordConfirmation
    }), () => this.loadProgress());
  }

  googleAccount(credential: string): void {
    const call = this.stage === 'login'
      ? this.api.googleSignIn(credential)
      : this.api.createAccount(this.token, { google_credential: credential });
    this.run(call, () => this.loadProgress());
  }

  signIn(): void {
    this.run(this.api.signIn(this.email, this.password), () => this.loadProgress(),
      'E-mail ou senha incorretos.');
  }

  signOut(): void {
    this.api.signOut();
    this.progress = null;
    this.stage = this.lead && this.lead.account ? 'login' : 'account';
  }

  // --- wizard ------------------------------------------------------------

  loadProgress(): void {
    this.loading = true;
    this.api.progress().subscribe({
      next: progress => {
        this.loading = false;
        this.applyProgress(progress);
        this.stage = this.firstOpenStep();
      },
      error: err => {
        this.loading = false;
        if (err.status === 401) {
          this.signOut();
          this.lead = this.lead && { ...this.lead, account: true };
          this.stage = 'login';
          return;
        }
        this.error = apiError(err);
      }
    });
  }

  saveCompany(): void {
    if (this.company.computer_os === 'mac') {
      this.error = 'Ainda não temos suporte para computadores Apple. Conclua o cadastro em um computador com ' +
        'Ubuntu 26 ou mais recente, ou com Windows 11.';
      return;
    }
    this.saveStep('company', this.company, 'plan');
  }

  savePlan(): void {
    this.saveStep('plan', { plan: this.selectedPlan }, 'personal');
  }

  savePersonal(): void {
    this.saveStep('personal', this.personal, 'contract');
  }

  accept(): void {
    if (!this.accepted || !this.progress || this.loading) {
      return;
    }
    this.loading = true;
    this.error = '';
    const version = this.progress.contract.version;
    this.turnstile.getToken().then(
      token => this.api.accept(version, token).subscribe({
        next: result => {
          this.loading = false;
          this.result = result;
          this.stage = 'done';
          window.scrollTo(0, 0);
        },
        error: err => {
          this.loading = false;
          if (err.status === 409) {
            this.accepted = false;
            this.loadProgress();
          }
          this.error = apiError(err);
        }
      }),
      () => {
        this.loading = false;
        this.error = 'Não foi possível verificar o navegador. Atualize a página e tente de novo.';
      }
    );
  }

  goTo(stage: Stage): void {
    if (this.canOpen(stage)) {
      this.error = '';
      this.stage = stage;
    }
  }

  canOpen(stage: Stage): boolean {
    const s = this.progress && this.progress.steps;
    if (!s) {
      return false;
    }
    switch (stage) {
      case 'company': return true;
      case 'plan': return s.company;
      case 'personal': return s.company && s.plan;
      case 'contract': return s.company && s.plan && s.personal;
      default: return false;
    }
  }

  stepDone(stage: Stage): boolean {
    const s = this.progress && this.progress.steps;
    return !!s && ((stage === 'company' && s.company) || (stage === 'plan' && s.plan) || (stage === 'personal' && s.personal));
  }

  inWizard(): boolean {
    return ['company', 'plan', 'personal', 'contract'].indexOf(this.stage) !== -1;
  }

  plan(key: string | null | undefined): Plan | undefined {
    return this.progress ? this.progress.plans.find(p => p.key === key) : undefined;
  }

  downloadUrl(): string {
    return this.result ? this.api.downloadUrl(this.result.install_code) : '';
  }

  newEstablishment(): void {
    this.result = null;
    this.company = this.blankCompany();
    this.selectedPlan = '';
    this.accepted = false;
    this.loadProgress();
  }

  private saveStep(step: 'company' | 'plan' | 'personal', body: object, next: Stage): void {
    this.run(this.api.saveStep(step, body), progress => {
      this.applyProgress(progress);
      this.stage = next;
      window.scrollTo(0, 0);
    });
  }

  private applyProgress(progress: SignupProgress): void {
    this.progress = progress;
    if (progress.company) {
      this.company = { ...this.blankCompany(), ...progress.company };
      this.selectedPlan = progress.company.plan || this.selectedPlan;
    }
    // the plan picked on the home page comes selected
    if (!this.selectedPlan && progress.chosen_plan) {
      this.selectedPlan = progress.chosen_plan;
    }
    this.personal = {
      name: progress.owner.name || '',
      cpf: this.personal.cpf || progress.owner.cpf || '',
      whatsapp: this.personal.whatsapp || progress.owner.whatsapp || ''
    };
  }

  private firstOpenStep(): Stage {
    const s = this.progress!.steps;
    if (!s.company) { return 'company'; }
    if (!s.plan) { return 'plan'; }
    if (!s.personal) { return 'personal'; }
    return 'contract';
  }

  private run<T>(call: Observable<T>, done: (value: T) => void, fallback?: string): void {
    if (this.loading) {
      return;
    }
    this.loading = true;
    this.error = '';
    call.subscribe({
      next: value => {
        this.loading = false;
        done(value);
      },
      error: err => {
        this.loading = false;
        // Devise Token Auth answers a wrong password in English.
        this.error = fallback && err.status === 401 ? fallback : apiError(err, fallback);
      }
    });
  }

  private blankCompany(): SignupCompany {
    return { nome_fantasia: '', razao_social: '', cnpj: '', address: '', district: '', city: '',
             computer_os: this.onMac ? 'mac' : '' };
  }
}
